import {
    DocumentScanLlmTraceReference,
    DocumentScanLlmTraceResult,
} from 'common/lib/model/documentScan/DocumentScanTrace';

export interface LlmTraceMessageViewModel {
    role: string;
    content: unknown;
}

export interface LlmTraceRequestViewModel {
    provider: string | null;
    model: string | null;
    durationMs: number | null;
    messages: LlmTraceMessageViewModel[];
    responseContent: unknown;
}

export interface LlmTraceViewModel {
    key: string;
    createdAt: string | null;
    unavailable: boolean;
    errorMessage: string | null;
    provider: string | null;
    model: string | null;
    temperature: number | null;
    structuredOutputEnabled: boolean | null;
    structuredOutputSchemaName: string | null;
    durationMs: number | null;
    usage: Record<string, unknown> | null;
    documentScanTypeVersionId: string | null;
    documentScanTypeVersionNumber: number | null;
    facilityDocumentScanTypeVersionId: string | null;
    facilityDocumentScanTypeVersionNumber: number | null;
    imageAssetIds: string[];
    ocrAssetIds: string[];
    requests: LlmTraceRequestViewModel[];
    rawEnvelope: DocumentScanLlmTraceResult;
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value : null;
}

function asNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringArray(value: unknown): string[] {
    return asArray(value).filter((item): item is string => typeof item === 'string');
}

function asUsage(value: unknown): Record<string, unknown> | null {
    const record = asRecord(value);
    return Object.keys(record).length ? record : null;
}

function firstDefined<T>(...values: T[]): T | null {
    return values.find((value) => value !== undefined && value !== null) ?? null;
}

function getResponseContent(requestTrace: Record<string, unknown>): unknown {
    const response = asRecord(requestTrace.response);
    const choices = asArray(response.choices);
    const firstChoice = asRecord(choices[0]);
    const message = asRecord(firstChoice.message);
    return message.content ?? '';
}

function getRequestMessages(requestTrace: Record<string, unknown>): LlmTraceMessageViewModel[] {
    const request = asRecord(requestTrace.request);
    return asArray(request.messages).map((message, index) => {
        const messageRecord = asRecord(message);
        return {
            role: asString(messageRecord.role) || `Message ${index + 1}`,
            content: messageRecord.content ?? '',
        };
    });
}

function getStructuredOutputValue(
    trace: Record<string, unknown>,
    reference: DocumentScanLlmTraceReference,
    key: 'enabled' | 'schemaName',
): boolean | string | null {
    const traceStructuredOutput = asRecord(trace.structuredOutput);
    const referenceStructuredOutput = reference.structuredOutput || null;
    return firstDefined(traceStructuredOutput[key], referenceStructuredOutput?.[key]) as boolean | string | null;
}

export function buildLlmTraceViewModels(traceResults: DocumentScanLlmTraceResult[]): LlmTraceViewModel[] {
    return traceResults.map((traceEnvelope, traceIndex) => {
        const trace = asRecord(traceEnvelope.trace);
        const reference = traceEnvelope.reference;
        const traceUsage = asUsage(trace.usage);
        const referenceUsage = asUsage(reference.usage);
        const requests = asArray(trace.requests).map((requestTrace) => {
            const requestTraceRecord = asRecord(requestTrace);
            return {
                provider:
                    asString(requestTraceRecord.provider) || asString(trace.provider) || reference.provider || null,
                model: asString(requestTraceRecord.model) || asString(trace.model) || reference.model || null,
                durationMs: asNumber(requestTraceRecord.durationMs),
                messages: getRequestMessages(requestTraceRecord),
                responseContent: getResponseContent(requestTraceRecord),
            };
        });

        return {
            key: reference.traceKey || `trace-${traceIndex}`,
            createdAt: asString(trace.createdAt) || reference.createdAt || null,
            unavailable: traceEnvelope.unavailable === true,
            errorMessage: traceEnvelope.errorMessage || null,
            provider: asString(trace.provider) || reference.provider || null,
            model: asString(trace.model) || reference.model || null,
            temperature: asNumber(trace.temperature) ?? reference.temperature ?? null,
            structuredOutputEnabled: getStructuredOutputValue(trace, reference, 'enabled') as boolean | null,
            structuredOutputSchemaName: getStructuredOutputValue(trace, reference, 'schemaName') as string | null,
            durationMs: asNumber(trace.durationMs) ?? reference.durationMs ?? null,
            usage: traceUsage || referenceUsage,
            documentScanTypeVersionId:
                asString(trace.documentScanTypeVersionId) || reference.documentScanTypeVersionId || null,
            documentScanTypeVersionNumber:
                asNumber(trace.documentScanTypeVersionNumber) ?? reference.documentScanTypeVersionNumber ?? null,
            facilityDocumentScanTypeVersionId:
                asString(trace.facilityDocumentScanTypeVersionId) ||
                reference.facilityDocumentScanTypeVersionId ||
                null,
            facilityDocumentScanTypeVersionNumber:
                asNumber(trace.facilityDocumentScanTypeVersionNumber) ??
                reference.facilityDocumentScanTypeVersionNumber ??
                null,
            imageAssetIds: asStringArray(trace.imageAssetIds).length
                ? asStringArray(trace.imageAssetIds)
                : reference.imageAssetIds || [],
            ocrAssetIds: asStringArray(trace.ocrAssetIds).length
                ? asStringArray(trace.ocrAssetIds)
                : reference.ocrAssetIds || [],
            requests,
            rawEnvelope: traceEnvelope,
        };
    });
}
